import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, Loader2 } from "lucide-react";
import { Img } from "../common/Img";
import { Reveal } from "../common/Reveal";
import ProductActions from "../common/ProductActions";
import { seasonPicksService } from "../../services/catalogService";
import { iconFor } from "../../lib/iconLibrary";
import { useLanguage } from "../../context/LanguageContext";

// Task 07 (C07) — Season Picks is admin-managed: the block shows the
// varieties matching the collection + color chosen in the admin panel.
export default function SeasonPicks() {
  const { t, localize } = useLanguage();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [active, setActive] = useState(0);

  useEffect(() => {
    seasonPicksService.getPublic().then(setData).catch(() => setData({ products: [], config: {} }));
  }, []);

  const products = data?.products || [];
  const p = products[Math.min(active, Math.max(products.length - 1, 0))];

  if (data === null) {
    return (
      <section className="py-24 md:py-32 bg-[#F3EEE7]" data-testid="season-picks-section">
        <div className="container-tessa text-center text-[#817B74]">
          <Loader2 className="animate-spin inline mr-2" size={16} />Loading…
        </div>
      </section>
    );
  }
  if (!products.length) return null;

  const openCollection = () => {
    const params = new URLSearchParams();
    if (data?.config?.category_id) params.set("categories", data.config.category_id);
    if (data?.config?.color_family) params.set("colors", data.config.color_family);
    navigate(`/catalog${params.toString() ? `?${params.toString()}` : ""}`);
  };

  return (
    <section
      className="relative py-24 md:py-32 transition-colors duration-700"
      style={{ backgroundColor: p.tint }}
      data-testid="season-picks-section"
    >
      <div className="container-tessa">
        <Reveal>
          <span className="font-sans text-xs uppercase tracking-[0.24em] text-[#D92A47]">
            {t("season.eyebrow")}
          </span>
        </Reveal>

        <div className="mt-10 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-6 items-center">
          {/* Left — text/data */}
          <div className="lg:col-span-4">
            <AnimatePresence mode="wait">
              <motion.div
                key={p.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -12 }}
                transition={{ duration: 0.5 }}
              >
                <h2 className="font-serif text-5xl md:text-6xl leading-[0.95] text-[#252525]">
                  {p.name}
                </h2>
                <p className="font-serif italic text-2xl text-[#D92A47] mt-1">{localize(p, "subtitle")}</p>
                <p className="mt-6 text-[#817B74] leading-relaxed max-w-sm">
                  {localize(p, "description")}
                </p>

                <div className="mt-8 space-y-4 text-sm">
                  {p.qualities?.length > 0 && (
                    <div data-testid="season-qualities">
                      <span className="uppercase tracking-[0.14em] text-[11px] text-[#817B74] block">
                        {t("season.idealPara")}
                      </span>
                      <div className="flex flex-wrap gap-2 mt-2">
                        {p.qualities.map((q, i) => {
                          const QIcon = iconFor(q.icon);
                          return (
                            <span
                              key={`${q.icon}-${i}`}
                              className="inline-flex items-center gap-2 bg-white/70 border border-black/10 px-3 py-1.5 text-[13px] text-[#252525]"
                              data-testid={`season-quality-${i}`}
                            >
                              <QIcon size={14} className="text-[#D92A47]" />
                              {q.label_en}
                            </span>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>

                <button
                  onClick={openCollection}
                  data-testid="season-view-collection"
                  className="group mt-9 inline-flex items-center gap-2 text-[13px] font-semibold uppercase tracking-[0.14em] text-[#D92A47]"
                >
                  {t("season.viewCollection")}
                  <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
                </button>

                <div className="mt-6">
                  <ProductActions
                    variety={{
                      id: p.id,
                      name: p.name,
                      image: p.appliedImage,
                      category_label: localize(p, "subtitle"),
                    }}
                  />
                </div>
              </motion.div>
            </AnimatePresence>

            {/* Product switcher */}
            <div className="mt-12 flex flex-wrap gap-3" role="tablist" aria-label="Season products">
              {products.map((prod, i) => (
                <button
                  key={prod.id}
                  role="tab"
                  aria-selected={i === active}
                  onClick={() => setActive(i)}
                  data-testid={`season-tab-${prod.id}`}
                  className={`text-[11px] uppercase tracking-[0.14em] pb-1 border-b-2 transition-colors ${
                    i === active
                      ? "border-[#D92A47] text-[#252525]"
                      : "border-transparent text-[#817B74] hover:text-[#252525]"
                  }`}
                >
                  {prod.name}
                </button>
              ))}
            </div>
          </div>

          {/* Center — applied floral */}
          <div className="lg:col-span-5">
            <AnimatePresence mode="wait">
              <motion.div
                key={p.id + "-applied"}
                initial={{ opacity: 0, scale: 1.03 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.6 }}
                className="relative aspect-[3/4] overflow-hidden"
              >
                <Img src={p.appliedImage} alt={`${p.name} applied in a floral setting`} className="h-full w-full object-cover" />
              </motion.div>
            </AnimatePresence>
          </div>

          {/* Right — macro */}
          <div className="lg:col-span-3">
            <AnimatePresence mode="wait">
              <motion.div
                key={p.id + "-macro"}
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.6, delay: 0.05 }}
                className="relative aspect-[3/5] overflow-hidden"
              >
                <Img src={p.macroImage} alt={`${p.name} macro detail`} className="h-full w-full object-cover" />
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </div>
    </section>
  );
}
